import get from "lodash.get";
import { FilterOption } from "node_modules/@flaminem/ui-components/dist/lib/Inputs/FilterablePicker/FilterablePicker.types";
import {
  Control,
  Controller,
  FieldPath,
  FieldValues,
  RegisterOptions,
  useFormState,
} from "react-hook-form";

import { FilterableSinglePicker } from "src/components/Pickers/FilterableSinglePicker";
import { useT } from "src/internationalization";

export type FilterableSinglePickerFormFieldProps<
  TItem,
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = {
  id: string;
  name: TName;
  control: Control<TFieldValues>;
  rules?: Omit<
    RegisterOptions<TFieldValues, TName>,
    "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
  >;
  label?: string;
  options: TItem[];
  helperText?: string;
  placeholder?: string;
  optional?: boolean;
  onChange?: () => void;
  getOptionLabel: (item: TItem) => string;
  getOptionValue: (item: TItem) => string;
  fullOption?: boolean;
  readOnly?: boolean;
  loading?: boolean;
  noOptionsText?: string;
  filterOption?: FilterOption<TItem>;
};

export function FilterableSinglePickerFormField<
  TItem,
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  id,
  name,
  options,
  filterOption,
  label,
  control,
  helperText,
  placeholder,
  optional = false,
  getOptionLabel,
  getOptionValue,
  onChange,
  readOnly,
  fullOption = true,
  loading,
  rules,
  noOptionsText,
}: FilterableSinglePickerFormFieldProps<TItem, TFieldValues, TName>) {
  const t = useT();
  const { errors } = useFormState({ control });
  const error = get(errors, name);

  return (
    <Controller<TFieldValues, TName>
      control={control}
      name={name}
      rules={rules}
      render={({ field: { value, onChange: onValueChange } }) => {
        return (
          <FilterableSinglePicker
            id={id}
            filterOption={filterOption}
            placeholder={placeholder}
            noOptionsText={noOptionsText}
            options={options}
            getOptionLabel={getOptionLabel}
            getOptionValue={getOptionValue}
            loading={loading}
            value={
              value === null
                ? null
                : fullOption
                ? options.find((item) => getOptionValue(item) === getOptionValue(value)) ?? null
                : options.find((item) => getOptionValue(item) === value) ?? null
            }
            label={
              label
                ? optional
                  ? `${label} ${t("general.form.label.optional")}`
                  : label
                : undefined
            }
            invalid={!!error}
            invalidText={error?.message as string}
            helperText={helperText}
            onChange={(selectedOption) => {
              if (fullOption) {
                onValueChange(selectedOption ?? null);
              } else {
                onValueChange(selectedOption ? getOptionValue(selectedOption) : null);
              }

              onChange?.();
            }}
            readOnly={readOnly}
          />
        );
      }}
    />
  );
}
